import React, { FC, useLayoutEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { NormalButton } from '@app/react/uikit';
import { Background3d } from '@component/Background3d';
import { makiwaraShipDateAlert } from '@app/react/action/MakiwaraShipDateAlert';
import {
    ArrowType,
    KINTEKI,
    ENTEKI,
    MAKIWARA,
    FULL,
    SEMI,
} from '@app/react/const/type';
import type { AppState } from '@app/react/reducer';
import { convertShippingDate } from '@app/react/common/convertShippingDate';
import { formatShort } from '@app/react/common/dateFormat';
import { ASSETS_PATH } from '@app/react/const/variables';
import { addFullEcHeaderNaviRole, removeFullEcHeaderNaviRole } from '@app/react/common/Function';
import { fetchInitProductSetItemAction } from '@app/react/action';
import { useQuery } from '@app/react/hooks/useQuery';
import { useMediaMobile } from '@app/react/common/useMedia';

const selectShippingDate = (state: AppState) => state.shippingDate;
const initArrowType = (state: AppState) => state.uiCustomDesign.initProductSetArrowType;
const initOrderType = (state: AppState) => state.uiCustomDesign.initProductSetOrderType;

interface OrdermadeArrowTypeProps {
    onKinteki: React.MouseEventHandler<HTMLInputElement>;
    onEnteki: React.MouseEventHandler<HTMLInputElement>;
    onMakiwara: React.MouseEventHandler<HTMLInputElement>;
}

const OrdermadeArrowType: FC<OrdermadeArrowTypeProps> = (props) => {
    const { onKinteki, onEnteki, onMakiwara } = props;

    return (
        <div className="ordermade-home-block">
            <p className="ordermade-home-p-3">
                <img
                    className="ordermade-home-badge1"
                    src={`${ASSETS_PATH}/svg/order-top-badge1.svg`}
                    alt="誰でも簡単に作れる"
                />
                今すぐカスタマイズ
            </p>
            <p className="ordermade-home-p-4">
                まずは矢の種類を選択してください。
            </p>
            <div className="ordermade-home-div">
                <NormalButton
                    title="近的矢"
                    title2={(
                        <img
                            src={`${ASSETS_PATH}/svg/button-right-icon.svg`}
                            alt="icon"
                        />
                    )}
                    classes="minimum-button"
                    onClick={onKinteki}
                />
                <NormalButton
                    title="遠的矢"
                    title2={(
                        <img
                            src={`${ASSETS_PATH}/svg/button-right-icon.svg`}
                            alt="icon"
                        />
                    )}
                    classes="minimum-button"
                    onClick={onEnteki}
                />
                <NormalButton
                    title="巻藁矢"
                    title2={(
                        <img
                            src={`${ASSETS_PATH}/svg/button-right-icon.svg`}
                            alt="icon"
                        />
                    )}
                    classes="minimum-button"
                    onClick={onMakiwara}
                />
            </div>
        </div>
    );
};

interface OrdermadeOrderTypeProps {
    fullOrderEstimateDate: string;
    semiOrderEstimateDate: string;
    onFullOrder: React.MouseEventHandler<HTMLInputElement>;
    onSemiOrder: React.MouseEventHandler<HTMLInputElement>;
    expressEnabled: boolean;
    expressDisableSummary: string;
}

const OrdermadeOrderType: FC<OrdermadeOrderTypeProps> = (props) => {
    const {
        fullOrderEstimateDate,
        semiOrderEstimateDate,
        onFullOrder,
        onSemiOrder,
        expressEnabled,
        expressDisableSummary,
    } = props;

    const expressDisable = { disabled: !expressEnabled };

    return (
        <div className="ordermade-order-type">
            <p className="header">
                <img
                    className="ordermade-home-badge2"
                    src={`${ASSETS_PATH}/svg/order-top-badge2.svg`}
                    alt="多くのカスタムを楽しめる"
                />
                オーダーの種類を選択
                <img
                    className="ordermade-home-badge3"
                    src={`${ASSETS_PATH}/svg/order-top-badge3.svg`}
                    alt="とにかくお急ぎの方こちら"
                />
            </p>

            <div className="flex-horizontal">
                <div className="flex-vertical">
                    <p className="sub-header">選択に制限なし&通常料金</p>
                    <NormalButton
                        title="フルオーダー"
                        title2={(
                            <img
                                src={`${ASSETS_PATH}/svg/button-right-icon.svg`}
                                alt="icon"
                            />
                        )}
                        classes="minimum-button type2"
                        onClick={onFullOrder}
                    />
                    <p className="estimate">
                        発送予定日：
                        {fullOrderEstimateDate}
                    </p>
                </div>
                <div className="flex-vertical">
                    <p className={classNames('sub-header', expressDisable)}>選択に制限あり&料金割増</p>
                    <NormalButton
                        title="特急オーダー"
                        title2={(
                            <img
                                src={`${ASSETS_PATH}/svg/button-right-icon.svg`}
                                alt="icon"
                            />
                        )}
                        classes="minimum-button type2"
                        onClick={onSemiOrder}
                        disabled={!expressEnabled}
                    />
                    <p className={classNames('estimate', expressDisable)}>
                        発送予定日：
                        {expressEnabled
                        ? semiOrderEstimateDate
                        : '--月--日'}
                    </p>
                </div>
            </div>
            <div className="div-annotation">
                <p className="annotation red">
                    {!expressEnabled && expressDisableSummary}
                </p>

                <p className="annotation">
                    ※発送予定日は本日中（
                    {formatShort(new Date())}
                    ）にご注文が確定した場合に限ります。
                </p>
                <p className="annotation">
                    ※発送予定日は注文状況によって前後する場合があります。
                </p>
                <p className="annotation">
                    ※プレビュー画像はイメージです。実際の商品とは異なる場合があります。
                </p>
                <p className="annotation">
                    ※当店は全国の加盟店と連携し充実したサポート体制を構築しております。
                    <br className="sp-none" />
                    購入手続きの際には必ずサポート店を選択してください。
                </p>
            </div>
        </div>
    );
};

interface TopHistoryState {
    flip: boolean;
    arrowType: ArrowType | null;
}

const initHistoryState = {
    flip: false,
    arrowType: null,
};

export const OrderTop: FC = () => {
    const dispatch = useDispatch();
    const query = useQuery();
    const isMobile = useMediaMobile();
    const history = useHistory<TopHistoryState>();
    const { flip, arrowType } = history.location.state || initHistoryState;

    // redirect to preview via rid query request
    const xArrowType = useSelector(initArrowType)
    const xOrderType = useSelector(initOrderType)
    if (xArrowType && xOrderType) {
        const rid = query.get('rid');
        console.log(`redirect to preview via rid(${xArrowType}/${xOrderType}/${rid})`);
        dispatch(fetchInitProductSetItemAction.started({ rid: rid }));
        const url = `/${xArrowType}/${xOrderType}/parts/preview`;
        history.push(url);
    }

    const shippingDate = useSelector(selectShippingDate);
    const { full, semi } = useMemo(() => {
        const { full: f, semi: s } = convertShippingDate(shippingDate, arrowType);
        return {
            full: f ?? '未設定',
            semi: s ?? '未設定',
        };
    }, [shippingDate, arrowType]);

    useLayoutEffect(() => {
        addFullEcHeaderNaviRole();

        return () => {
            removeFullEcHeaderNaviRole();
        };
    }, []);

    const expressInfos = useSelector((state: AppState) => (
        state.expressOrder.filter((info) => info.arrow_type === arrowType)
    ));
    // バックエンドの実装的に常に1つなので、index 0 へアクセスする。
    const expressEnabled = expressInfos[0]?.enable ?? true;
    const expressDisableSummary = expressInfos[0]?.description ?? '';

    const onKinteki = () => {
        history.push('', { flip: true, arrowType: KINTEKI });
    };
    const onEnteki = () => {
        history.push('', { flip: true, arrowType: ENTEKI });
    };
    const onMakiwara = () => {
        dispatch(makiwaraShipDateAlert({ show: true }));
        history.push(`/${MAKIWARA}/${FULL}/parts`);
    };
    const onFullOrder = () => {
        if (arrowType === null) {
            console.error('arrow type is null');
            return;
        }
        history.push(`/${arrowType}/${FULL}/parts`);
    };
    const onSemiOrder = () => {
        if (arrowType === null) {
            console.error('arrow type is null');
            return;
        }
        history.push(`/${arrowType}/${SEMI}/parts`);
    };

    return (
        <section className="top_container">
            <Background3d />
            <div className="ordermade-home-contents-out-wrapper">
                <div className="ordermade-home-contents-head">
                    <p className="ordermade-home-p-1">
                        <img
                            src={`${ASSETS_PATH}/svg/order-top-yanoordermade.svg`}
                            alt="矢のオーダーメイド"
                        />
                    </p>
                    <p className="ordermade-home-p-2">
                        <img
                            src={`${ASSETS_PATH}/svg/order-top-designyourarrows.svg`}
                            alt="design your arrows"
                        />
                    </p>
                </div>
                <div className="ordermade-home-contents-content">
                    {flip === false ? (
                        <OrdermadeArrowType
                            onKinteki={onKinteki}
                            onEnteki={onEnteki}
                            onMakiwara={onMakiwara}
                        />
                    ) : (
                        <OrdermadeOrderType
                            fullOrderEstimateDate={full}
                            semiOrderEstimateDate={semi}
                            onFullOrder={onFullOrder}
                            onSemiOrder={onSemiOrder}
                            expressEnabled={expressEnabled}
                            expressDisableSummary={expressDisableSummary}
                        />
                    )}
                </div>
            </div>
        </section>
    );
};
